/**
 * The ingest's warnings, grouped by kind into one panel each.
 *
 * Rendered in two places that must read identically: the datasets page's
 * "Ingestion warnings" dialog (what the last sync recorded) and the dataset
 * form's pre-save preview (what a sync of the unsaved config WOULD record).
 * One component, so a warning seen before Save looks exactly like the same
 * warning seen after — and so adding a variant is one panel, not two.
 *
 * Workflow-level warnings (one fix resolves many items) render as a single
 * block each; per-item warnings render as a flat list under one heading.
 */

import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import type { ImportWarning } from '../types';

export function WarningPanels({ warnings }: { warnings: ImportWarning[] }) {
  const grouped = groupByKind(warnings);
  return (
    <>
      {grouped.departureColumnNotFound.map((w, i) => (
        <DepartureColumnPanel key={`dep-${i}`} warning={w} />
      ))}
      {grouped.unknownStatuses.map((w, i) => (
        <UnknownStatusesPanel key={`stat-${i}`} warning={w} />
      ))}
      {grouped.unparseableFields.length > 0 && <UnparseableFieldsPanel warnings={grouped.unparseableFields} />}
      {grouped.unresolvedPredecessors.length > 0 && (
        <UnresolvedPredecessorsPanel warnings={grouped.unresolvedPredecessors} />
      )}
      {grouped.ambiguousFeatureParents.length > 0 && (
        <AmbiguousFeatureParentsPanel warnings={grouped.ambiguousFeatureParents} />
      )}
    </>
  );
}

interface GroupedWarnings {
  departureColumnNotFound: Extract<ImportWarning, { kind: 'departure_column_not_found' }>[];
  unknownStatuses: Extract<ImportWarning, { kind: 'unknown_statuses' }>[];
  unparseableFields: Extract<ImportWarning, { kind: 'unparseable_field' }>[];
  unresolvedPredecessors: Extract<ImportWarning, { kind: 'unresolved_predecessor' }>[];
  ambiguousFeatureParents: Extract<ImportWarning, { kind: 'ambiguous_feature_parent' }>[];
}

function groupByKind(warnings: ImportWarning[]): GroupedWarnings {
  const out: GroupedWarnings = {
    departureColumnNotFound: [],
    unknownStatuses: [],
    unparseableFields: [],
    unresolvedPredecessors: [],
    ambiguousFeatureParents: [],
  };
  for (const w of warnings) {
    switch (w.kind) {
      case 'departure_column_not_found':
        out.departureColumnNotFound.push(w);
        break;
      case 'unknown_statuses':
        out.unknownStatuses.push(w);
        break;
      case 'unparseable_field':
        out.unparseableFields.push(w);
        break;
      case 'unresolved_predecessor':
        out.unresolvedPredecessors.push(w);
        break;
      case 'ambiguous_feature_parent':
        // Unlike the apply-only variants below, this one is raised by the
        // adapter while building the batch, so the ingest preview does see it.
        out.ambiguousFeatureParents.push(w);
        break;
      case 'empty_throughput':
      case 'shared_dataset_in_simulation':
      case 'feature_name_conflict':
      case 'unresolved_feature_parent':
        // Apply-only variants — emitted by the tracker-apply / feature-import
        // apply steps, never by the ingest preview this dialog renders. Listed
        // explicitly so the exhaustiveness guard below stays valid.
        break;
      default: {
        // Compile error if a new ImportWarning variant is added without a case
        // here (this is the real exhaustiveness guard the old comment claimed).
        const _exhaustive: never = w;
        void _exhaustive;
      }
    }
  }
  return out;
}

function PanelShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-sm border border-feedback-warning/40 bg-feedback-warning/5 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-feedback-warning" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium text-feedback-warning">{title}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function DepartureColumnPanel({
  warning,
}: {
  warning: Extract<ImportWarning, { kind: 'departure_column_not_found' }>;
}) {
  return (
    <PanelShell title="Departure column not found in workflow">
      <p className="text-text-muted">
        The adapter is configured with departure column <code>{warning.departure_column}</code>, but the workflow
        defines none with that exact name. Nothing will be counted as shipped until the names match (compare
        case-sensitively).
      </p>
      <p className="text-text-muted">
        Available columns:{' '}
        {warning.available_columns.length === 0 ? (
          <em>(none)</em>
        ) : (
          warning.available_columns.map((c, i) => (
            <span key={c}>
              {i > 0 ? ', ' : ''}
              <code>{c}</code>
            </span>
          ))
        )}
      </p>
    </PanelShell>
  );
}

function UnknownStatusesPanel({ warning }: { warning: Extract<ImportWarning, { kind: 'unknown_statuses' }> }) {
  return (
    <PanelShell title="Statuses not mapped to any workflow column">
      <p className="text-text-muted">
        These statuses appear in work items but aren't listed in any column of the workflow. Items in these statuses
        cannot reach the departure column <code>{warning.departure_column}</code>, so they will never count as shipped.
      </p>
      <ul className="ml-4 list-disc text-text-muted">
        {warning.statuses.map((s) => (
          <li key={s}>
            <code>{s}</code>
          </li>
        ))}
      </ul>
    </PanelShell>
  );
}

function UnparseableFieldsPanel({ warnings }: { warnings: Extract<ImportWarning, { kind: 'unparseable_field' }>[] }) {
  return (
    <PanelShell title={`Unparseable field values (${warnings.length})`}>
      <p className="text-text-muted">
        These values couldn't be parsed; the affected fields fall back to <code>null</code>.
      </p>
      <ul className="ml-4 list-disc text-text-muted">
        {warnings.map((w, i) => (
          <li key={`${w.source_id}-${w.field}-${i}`}>
            <code>{w.source_id}</code> · field <code>{w.field}</code> = <code>{w.raw_value}</code>
          </li>
        ))}
      </ul>
    </PanelShell>
  );
}

function UnresolvedPredecessorsPanel({
  warnings,
}: {
  warnings: Extract<ImportWarning, { kind: 'unresolved_predecessor' }>[];
}) {
  return (
    <PanelShell title={`Unresolved predecessors (${warnings.length})`}>
      <p className="text-text-muted">
        These items are referenced as predecessors but weren't included in the fetched batch — widen the query if you
        need the dependency to apply.
      </p>
      <ul className="ml-4 list-disc text-text-muted">
        {warnings.map((w, i) => (
          <li key={`${w.predecessor_source_id}-${w.successor_source_id}-${i}`}>
            <code>{w.predecessor_source_id}</code> blocks <code>{w.successor_source_id}</code>
          </li>
        ))}
      </ul>
    </PanelShell>
  );
}

function AmbiguousFeatureParentsPanel({
  warnings,
}: {
  warnings: Extract<ImportWarning, { kind: 'ambiguous_feature_parent' }>[];
}) {
  return (
    <PanelShell title={`Ambiguous feature parents (${warnings.length})`}>
      <p className="text-text-muted">
        These items name more than one feature parent. A deliverable belongs to exactly one feature, so the first was
        used — narrow the field to a single value if that is not the one you meant.
      </p>
      <ul className="ml-4 list-disc text-text-muted">
        {warnings.map((w, i) => (
          <li key={`${w.deliverable_source_id}-${i}`}>
            <code>{w.deliverable_source_id}</code> names <code>{w.candidate_source_ids.join(', ')}</code>
          </li>
        ))}
      </ul>
    </PanelShell>
  );
}
